import React, { useEffect, useState, useCallback } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import TimeTableInput from "./timeTableInput";
import { useMutation } from "@apollo/client";

const GET_MY_WEEK = gql`
    query GetMyWeek($weekNumber: Int!) {
        getMyWeek(weekNumber: $weekNumber) {
            weekNumber
            days {
                date
                weekNumber
                dayOfWeek
                dayName
                dayShortName
                holiday {
                    name
                }
                unitCells {
                    weekNumber
                    dayOfWeek
                    unitIndex
                    Editable
                    Content
                }
            }
            timeDescriptions {
                index
                startTime
                endTime
            }
            stats {
                exits
                studies
            }
        }
        me {
            ... on Student {
                id
                username
                surname
                lastname
                class {
                    id
                    name
                }
                editing
                boarding
            }
        }
        schoolYearOverview {
            normalStudentUnits
        }
    }
`;

const SET_MY_UNIT = gql`
    mutation SetMyUnit($weekNumber: Int!, $dayNumber: Int!, $unitNumber: Int!, $content: String!) {
        setMyUnit(weekNumber: $weekNumber, dayNumber: $dayNumber, unitNumber: $unitNumber, content: $content)
    }
`;

export default function StudentWeekView({ weekIndex, setStats = (exits, studies) => {} }) {
    const [fetchWeek, { data, loading, error }] = useLazyQuery(GET_MY_WEEK);
    const [setMyUnitMutation] = useMutation(SET_MY_UNIT);
    
    // Local state for table data to prevent constant re-rendering
    const [localWeekData, setLocalWeekData] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [userInfo, setUserInfo] = useState(null);
    const [schoolYearInfo, setSchoolYearInfo] = useState(null);

    // Update local state when server data changes
    useEffect(() => {
        if (data && data.getMyWeek) {
            setLocalWeekData(data.getMyWeek);
            setUserInfo(data.me);
            setSchoolYearInfo(data.schoolYearOverview);
            setIsInitialLoading(false);
            setStats(data.getMyWeek.stats.exits, data.getMyWeek.stats.studies);
        }
    }, [data]);

    useEffect(() => {
        if (weekIndex != null) {
            setIsInitialLoading(true);
            fetchWeek({ variables: { weekNumber: weekIndex } });
        }
    }, [weekIndex, fetchWeek]);

    // Helper to get cell content from local data
    const getCellValue = useCallback((dayIdx, unitIdx) => {
        if (!localWeekData || !localWeekData.days) return "";
        const cell = localWeekData.days[dayIdx]?.unitCells.find(c => c.unitIndex === unitIdx);
        return cell?.Content || "";
    }, [localWeekData]);

    // Helper to update local cell data immediately
    const updateLocalCellData = useCallback((dayIndex, unitIndex, newContent) => {
        if (!localWeekData) return;
        
        setLocalWeekData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            const day = updatedData.days[dayIndex];
            if (day) {
                let cell = day.unitCells.find(c => c.unitIndex === unitIndex);
                if (cell) {
                    cell.Content = newContent;
                } else {
                    // Create new cell if it doesn't exist
                    day.unitCells.push({
                        weekNumber: updatedData.weekNumber,
                        dayOfWeek: day.dayOfWeek,
                        unitIndex: unitIndex,
                        Editable: true,
                        Content: newContent
                    });
                }
            }
            return updatedData;
        });
    }, [localWeekData]);

    // Callback for cell edit - updates local state immediately, then syncs with server
    const handleCellChange = async (oldValue, newValue, timeUnitIndex, dayIndex) => {
        if (!localWeekData) return;
        
        // Update local state immediately for responsive UI
        updateLocalCellData(dayIndex, timeUnitIndex, newValue);
        
        const day = localWeekData.days[dayIndex];
        const weekNumber = localWeekData.weekNumber;
        const dayNumber = day.dayOfWeek;
        const unitNumber = timeUnitIndex;
        
        try {
            await setMyUnitMutation({
                variables: {
                    weekNumber,
                    dayNumber,
                    unitNumber,
                    content: newValue
                }
            });

            // Silently refetch data in background to sync with server
            // This won't cause UI flickering since we're using local state
            fetchWeek({ 
                variables: { weekNumber },
                fetchPolicy: 'network-only' // Force fresh data from server
            });
        } catch (err) {
            console.error("Fehler beim Speichern:", err);
            // Revert local state on error
            updateLocalCellData(dayIndex, timeUnitIndex, oldValue);
        }
    };

    if (weekIndex == null) return <div>Bitte eine Woche auswählen.</div>;
    if (isInitialLoading) return <div>Lade Wochenplan...</div>;
    if (error) return <div className="text-red-600">Fehler: {error.message}</div>;
    if (!localWeekData) return <div>Keine Daten für diese Woche.</div>;

    const isBoarding = userInfo?.boarding;
    const normalStudentUnits = schoolYearInfo?.normalStudentUnits || 0;
    const days = localWeekData.days;
    const timeUnits = localWeekData.timeDescriptions;

    function formatDate(dateString) {
        const date = new Date(dateString);
        if (isNaN(date)) return "";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear()).slice(-2);
        return `${day}.${month}.${year}`;
    }

    return (
        <div className="w-full overflow-x-auto">
            <table
                className="min-w-full text-base overflow-x-auto"
                style={{ borderCollapse: 'separate', borderSpacing: '4px 0px' }}
            >
                <thead>
                    <tr>
                        <th className="py-1 max-w-[64px]"> </th>
                        {days.map(day => {
                            const isHoliday = day.holiday != null;
                            return (
                                <th
                                    className="py-1 w-[110px] font-bold text-center border-b border-gray-600"
                                    key={day.dayOfWeek}
                                >
                                    {isHoliday ? (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <span className="text-gray-500 italic">{day.holiday.name}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="hidden md:inline">{day.dayName}</span>
                                            <span className="md:hidden">{day.dayShortName}</span>
                                            <p className="text-xs text-gray-500 italic">
                                                {formatDate(day.date)}
                                            </p>
                                        </>
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {timeUnits
                        .slice(
                            0,
                            isBoarding
                                ? timeUnits.length
                                : normalStudentUnits
                        )
                        .map((unit, rowIdx) => {
                            const { startTime, endTime } = unit;
                            return (
                                <tr key={rowIdx} className="h-[64px]">
                                    <td className="py-1 w-[64px] text-sm md:text-md font-bold text-left bg-white align-middle">
                                        <span className="hidden md:inline">{startTime} - {endTime}</span>
                                        <span className="md:hidden">{startTime} <br />{endTime}</span>
                                    </td>
                                    {days.map((day, colIdx) => {
                                        const cell = day.unitCells.find(c => c.unitIndex === rowIdx);
                                        const isHoliday = day.holiday != null;
                                        const top = rowIdx === 0 ? null : getCellValue(colIdx, rowIdx-1);
                                        const bottom = rowIdx === timeUnits.length - 1 ? null : getCellValue(colIdx, rowIdx+1);
                                        return (
                                            <td className="relative min-w-[64px] h-full p-0" key={colIdx}>
                                                {isHoliday ? (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                    </div>
                                                ) : (
                                                    <TimeTableInput
                                                        data={getCellValue(colIdx, rowIdx)}
                                                        editable={cell?.Editable}
                                                        dataTop={top}
                                                        dataBottom={bottom}
                                                        timeUnitIndex={rowIdx}
                                                        dayIndex={colIdx}
                                                        onChange={handleCellChange}
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
        </div>
    );
}
